// src/components/layout/ProtectedLayout.tsx
import { useState, useEffect } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHouse, faSun, faMoon, faAddressCard, faKey, faRightFromBracket, faFileContract } from '@/icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTerms } from '../../contexts/TermsContext';
import { useConfiguracaoLogin } from '../../hooks/useConfiguracaoLogin';
import { showConfirm } from '../../contexts/DialogContext';
import Modal from '../../components/common/Modal';
import PersonalDataForm from './PersonalDataForm';
import ChangePasswordForm from './ChangePasswordForm';
import '../../components/layout/ProtectedLayout.css';
import { formatDateTime, isValidDateString } from '../../utils/formatUtils';
import { initials } from '../../utils/initials';
import { getEnvironmentConfig } from '../../services/environment';
import { SUPPORT_URL } from '../../constants';

// Lido uma vez na carga do módulo: deriva de `window.location.hostname`, que não
// muda durante a sessão (premissa #5 — logout/401 fazem hard reload). Mesmo
// padrão de `api.ts`.
const { SUBDOMAIN } = getEnvironmentConfig();

// Saudação dinâmica baseada na hora local do navegador (consistente com `new Date().getFullYear()` no footer)
// 00:00:00 - 12:00:00 inclusive: Bom dia | 12:00:01 - 18:00:00 inclusive: Boa tarde | 18:00:01 - 23:59:59: Boa noite
const getGreeting = (date: Date): 'Bom dia' | 'Boa tarde' | 'Boa noite' => {
  const totalSeconds = date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
  if (totalSeconds <= 43200) return 'Bom dia';     // até 12:00:00
  if (totalSeconds <= 64800) return 'Boa tarde';   // até 18:00:00
  return 'Boa noite';
};

const ProtectedLayout = () => {
  const { theme, toggleTheme } = useTheme();
  const { logout, nome, nomeCompleto, userName, penultimoAcesso } = useAuth();
  // Item "Termos de Uso" no dropdown — visível só após o aceite vigente (issue #6).
  const { hasAccepted, openView } = useTerms();
  // Método de login do tenant (issue #29): oculta "Trocar Senha" quando só
  // Microsoft (permiteSenha=false). Fallback true — nunca esconde por erro/loading.
  const { config: loginConfig } = useConfiguracaoLogin();
  const permiteSenha = loginConfig?.permiteSenha ?? true;
  const userData = {
    // `nome` (primeiro nome) alimenta a SAUDAÇÃO à esquerda; `nomeCompleto`
    // alimenta a IDENTIDADE à direita. Separados de propósito: "Bom dia,
    // Raphael." lê melhor que "Bom dia, Raphael Fernandes.".
    nome: nome || 'Usuário',
    nomeCompleto: nomeCompleto || nome || 'Usuário',
    userName: userName || 'Desconhecido',
    ultimoAcesso: penultimoAcesso || 'Não registrado',
  };
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<'personalData' | 'changePassword' | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isMenuOpen) {
        const toggleBtn = document.getElementById('menu-toggle-btn');
        const dropdownMenu = document.querySelector('.dropdown-menu');
        if (
          toggleBtn &&
          !toggleBtn.contains(e.target as Node) &&
          dropdownMenu &&
          !dropdownMenu.contains(e.target as Node)
        ) {
          setIsMenuOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  const handleOpenModal = (content: 'personalData' | 'changePassword') => {
    setModalContent(content);
    setIsModalOpen(true);
    setIsMenuOpen(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalContent(null);
  };

  const handleLogout = async () => {
    const confirmed = await showConfirm('Deseja sair do sistema?', { title: 'Confirmar saída' });
    if (confirmed) {
      logout();
    }
  };

  return (
    <div className="protected-layout">
      <header>
        <nav id="bottom-header">
          <div id="bottom-header-inner">
            <div className="container-logo">
              <Link to="/home" className="home-icon-link">
                <FontAwesomeIcon icon={faHouse} size="2x" />
              </Link>
              <span className="icon-home">{getGreeting(new Date())}, {userData.nome}.</span>
            </div>
            <div id="bottom-header-links">
              <button id="theme-toggle-btn" onClick={toggleTheme}>
                {theme === 'dark' ? <FontAwesomeIcon icon={faSun} size="2x" /> : <FontAwesomeIcon icon={faMoon} size="2x" />}
              </button>
              {/* Identidade no topo direito (home-mockup-v2). O `id` do botão é
                  PRESERVADO — 2 specs e2e e 3 pontos da suíte visual dependem
                  dele; só o conteúdo trocou (faBars → monograma de iniciais).
                  Não há endpoint de avatar, então a inicial é o único
                  identificador visual possível.

                  Usa `nomeCompleto` (claim do JWT), não `nome`: o monograma
                  precisa de 2 palavras para render 2 iniciais. */}
              <div className="header-identity">
                <div className="header-identity-who">
                  <b>{userData.nomeCompleto}</b>
                  <small>UserName: {userData.userName}</small>
                </div>
                <button
                  id="menu-toggle-btn"
                  aria-label="Menu da conta"
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                >
                  {initials(userData.nomeCompleto)}
                </button>
              </div>
              {isMenuOpen && (
                <div className="dropdown-menu">
                  <button
                    className="dropdown-link"
                    onClick={() => handleOpenModal('personalData')}
                  >
                    <FontAwesomeIcon icon={faAddressCard} /> <div>Dados Pessoais</div>
                  </button>
                  {permiteSenha && (
                    <button
                      className="dropdown-link"
                      onClick={() => handleOpenModal('changePassword')}
                    >
                      <FontAwesomeIcon icon={faKey} /> <div>Trocar Senha</div>
                    </button>
                  )}
                  {hasAccepted && (
                    <button
                      className="dropdown-link"
                      onClick={() => {
                        openView();
                        setIsMenuOpen(false);
                      }}
                    >
                      <FontAwesomeIcon icon={faFileContract} /> <div>Termos de Uso</div>
                    </button>
                  )}
                  <hr />
                  <button className="dropdown-logout-btn" onClick={handleLogout}>
                    <FontAwesomeIcon icon={faRightFromBracket} /> Sair
                  </button>
                </div>
              )}
            </div>
          </div>
        </nav>
      </header>
      <main className="layout-content">
        <Outlet />
      </main>
      <footer>
        {/* Slot esquerdo: o `UserName:` migrou para o header (home-mockup-v2) e
            o lugar passou a exibir o TENANT. Premissa #1 — subdomínio = tenant =
            banco isolado; saber em qual tenant se está é informação operacional
            obrigatória, e a URL nem sempre está visível (janela sem barra de
            endereço, print de tela, suporte remoto). */}
        <div className="footer-slot-left">Tenant: {SUBDOMAIN || '—'}</div>
        <div className="footer-inner">
          <p>
            © {new Date().getFullYear()} Bluelt. Todos os direitos reservados. |{' '}
            <a href={SUPPORT_URL}>
              Precisa de ajuda?
            </a>
          </p>
        </div>
        <div>Último acesso: {userData.ultimoAcesso && userData.ultimoAcesso !== 'Não registrado' && isValidDateString(userData.ultimoAcesso) ? formatDateTime(userData.ultimoAcesso) : 'Não registrado'}</div>
      </footer>
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} theme={theme}>
        {modalContent === 'personalData' && <PersonalDataForm theme={theme} onClose={handleCloseModal} />}
        {modalContent === 'changePassword' && <ChangePasswordForm theme={theme} onClose={handleCloseModal} />}
      </Modal>
    </div>
  );
};

export default ProtectedLayout;
